import {Link} from 'react-router-dom';
import {ArrowRight, Mail, MessageSquareText, Phone, type LucideIcon} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@common-web/components/ui/accordion';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {renderRichText} from '@client/pages/public/enroll/richText';
import type {FaqItem} from '@client/pages/public/enroll/types';
import {siteFaq} from '@common/enrollmentSite/defaults/faq';

export function EnrollFaq() {
  const group = useEnrollGroup();
  const base = `/${group.slug}`;
  const phoneDigits = group.supportPhone.replace(/\D/g, '');
  const sections = siteFaq(group);

  return (
    <div className={`mx-auto max-w-7xl space-y-10 ${POSTHOG_UNMASK_CLASS}`}>
      <header className='space-y-3'>
        <span className='inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold tracking-wide text-blue-700'>
          Help center
        </span>
        <h1 className='text-4xl font-bold tracking-tight text-[var(--enroll-ink)]'>
          FAQs <span className='text-[var(--enroll-accent)]'>—</span> The answers you need
        </h1>
        <p className='max-w-2xl text-neutral-500'>
          Quick answers about enrollment dates, plan changes, ID cards, EOBs, and how to reach the
          Concierge Team. Can't find what you need? Call us at{' '}
          <a
            href={`tel:${phoneDigits}`}
            className='font-semibold text-[var(--enroll-primary)] hover:underline'
          >
            {group.supportPhone}
          </a>
          .
        </p>
      </header>

      {sections.map((section) => (
        <section key={section.label} className='space-y-3'>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-[var(--enroll-accent)]'>
            {section.label}
          </p>
          <Accordion type='multiple' className='rounded-2xl border border-neutral-200 bg-white px-5'>
            {section.items.map((item) => (
              <AccordionItem key={item.question} value={item.question} className='last:border-b-0'>
                <AccordionTrigger className='text-left text-base font-semibold text-neutral-900'>
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className='text-base leading-relaxed text-neutral-600'>
                  <FaqAnswer item={item} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}

      <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
        <h2 className='text-xl font-semibold text-[var(--enroll-ink)]'>Still have questions?</h2>
        <p className='mt-1 text-sm text-neutral-500'>
          The Concierge Team is here to help with anything not covered above — benefits, enrollment,
          ID cards, providers, or prescriptions.
        </p>
        <div className='mt-5 grid gap-3 sm:grid-cols-3'>
          <ContactTile
            Icon={Phone}
            label='Call'
            value={group.supportPhone}
            href={`tel:${phoneDigits}`}
          />
          {group.supportText && (
            <ContactTile
              Icon={MessageSquareText}
              label='Text'
              value={group.supportText}
              href={`sms:${group.supportText.replace(/\D/g, '')}`}
            />
          )}
          {group.supportEmail && (
            <ContactTile
              Icon={Mail}
              label='Email'
              value={group.supportEmail}
              href={`mailto:${group.supportEmail}`}
            />
          )}
        </div>
      </section>

      <div className='flex justify-center'>
        <Link
          to={`${base}/medical`}
          className='inline-flex h-12 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-7 text-base font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
        >
          Ready to enroll
          <ArrowRight className='h-[18px] w-[18px]' />
        </Link>
      </div>
    </div>
  );
}

function FaqAnswer({item}: {item: FaqItem}) {
  return (
    <div className='space-y-2'>
      <p>{renderRichText(item.answer)}</p>
      {item.bullets && (
        <ul className='space-y-1.5'>
          {item.bullets.map((bullet) => (
            <li key={bullet} className='flex gap-2'>
              <span className='mt-2 h-1.5 w-1.5 flex-none rounded-full bg-neutral-400' />
              <span>{renderRichText(bullet)}</span>
            </li>
          ))}
        </ul>
      )}
      {item.closing && <p>{renderRichText(item.closing)}</p>}
    </div>
  );
}

function ContactTile({
  Icon,
  label,
  value,
  href,
}: {
  Icon: LucideIcon;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className='flex items-center gap-3 rounded-xl border border-neutral-200 p-4 transition-colors hover:border-[var(--enroll-accent)]'
    >
      <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[var(--enroll-primary)]/10 text-[var(--enroll-primary)]'>
        <Icon className='h-[18px] w-[18px]' />
      </span>
      <span className='leading-tight'>
        <span className='block text-xs uppercase tracking-wider text-neutral-400'>{label}</span>
        <span className='block text-sm font-semibold text-neutral-900'>{value}</span>
      </span>
    </a>
  );
}
